/**
 * TeacherLessonPreview — the Blackboard / classroom-preview screen.
 *
 * Per Teacher Mode Build Spec §9 / §24A. Two routes mount this component:
 *   /teacher/today               — autoloads today's recommended lesson
 *   /teacher/lesson/:lessonId    — preview a specific lesson by id
 *
 * Shows: title · objective · target words · sentence pattern · challenge
 * goal · estimated duration · "Start lesson" CTA → opens a TeacherSessionRun
 * and routes to /teacher/run/{run_id}.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CaretLeft, ChalkboardTeacher, GraduationCap, BookOpen, Target,
  Lightning, ClockClockwise, ArrowRight, SpinnerGap, Notebook,
} from '@phosphor-icons/react';
import { http, apiErrorMessage } from '@/lib/api';

export default function TeacherLessonPreview({ today = false }) {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [reason, setReason] = useState('');
  const [instructionMode, setInstructionMode] = useState('');
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');
  // Free-tier daily quota for Teacher-Mode lessons. ``null`` for Pro
  // users (unlimited). Populated from /teacher/lessons/today.
  const [freeQuota, setFreeQuota] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        if (today) {
          const { data } = await http.get('/teacher/lessons/today');
          setLesson(data?.lesson || null);
          setReason(data?.reason || '');
          setFreeQuota(data?.free_quota || null);
        } else if (lessonId) {
          const { data } = await http.get(`/teacher/lessons/${lessonId}`);
          setLesson(data);
        }
      } catch (e) {
        setError(apiErrorMessage(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [today, lessonId]);

  async function startLesson() {
    if (!lesson) return;
    setStarting(true);
    setError('');
    try {
      const { data } = await http.post(
        `/teacher/lessons/${lesson.lesson_id}/start`, {},
      );
      // Phase 3 — capture the resolved mode in case the user lingered on
      // the preview after changing their Profile setting in another tab.
      if (data.instruction_language_mode) {
        setInstructionMode(data.instruction_language_mode);
      }
      navigate(`/teacher/run/${data.run.id}`, { replace: true });
    } catch (e) {
      // 402 Payment Required = the free daily quota is exhausted.
      // Redirect to pricing with a query param so the pricing page can
      // show a tailored "you hit the daily cap" banner at the top.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 402 && detail?.kind === 'free_teacher_daily_cap') {
        navigate('/pricing?reason=teacher_daily_cap', { replace: true });
        return;
      }
      setError(apiErrorMessage(e));
      setStarting(false);
    }
  }

  if (loading) {
    return (
      <div className="lf-shell flex items-center justify-center" data-testid="teacher-preview-loading">
        <SpinnerGap size={28} className="animate-spin text-[var(--lf-primary)]" />
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="lf-shell flex flex-col px-6 py-10" data-testid="teacher-preview-empty">
        <BackBar />
        <div className="lf-card mt-8 p-6 text-center" data-testid="teacher-preview-no-lesson">
          <GraduationCap size={32} weight="duotone" className="mx-auto text-[var(--lf-primary)]" />
          <div className="mt-3 text-lg font-bold">No lesson available yet</div>
          <p className="mt-1 text-sm text-[var(--lf-text-muted)]">
            {error || "We're updating the lesson catalog. Please try again in a moment."}
          </p>
          <Link to="/home" className="lf-btn lf-btn-primary mt-5 inline-flex">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="lf-shell flex flex-col px-6 py-6" data-testid="teacher-preview-page">
      <BackBar />

      {/* Reason badge for daily-lesson explanations */}
      {today && reason ? (
        <ReasonBadge reason={reason} />
      ) : null}

      {/* Blackboard-styled hero card */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="mt-5 rounded-3xl border border-[#3b362d]/40 bg-gradient-to-b from-[#1f2a26] to-[#13201d] p-6 shadow-2xl"
        data-testid="teacher-preview-blackboard"
      >
        <div className="flex items-center gap-2 text-[#86d6b4] text-xs uppercase tracking-widest">
          <ChalkboardTeacher size={16} weight="duotone" /> Today's lesson
        </div>
        <h1 className="mt-2 text-3xl font-bold text-white leading-tight" data-testid="teacher-preview-title">
          {lesson.title}
        </h1>
        <div className="mt-1 flex items-center gap-3 text-xs text-white/60">
          <span data-testid="teacher-preview-cefr">CEFR {lesson.cefr_band}</span>
          <span>·</span>
          <span className="inline-flex items-center gap-1">
            <ClockClockwise size={12} weight="bold" /> {lesson.duration_minutes} min
          </span>
          <span>·</span>
          <span className="capitalize">{(lesson.lesson_type || 'core').replace('_', ' ')}</span>
        </div>

        <div className="mt-5 rounded-2xl bg-white/5 p-4 border border-white/10">
          <div className="text-[10px] uppercase tracking-widest text-[#86d6b4]">Goal</div>
          <div className="mt-1 text-white/90 text-base" data-testid="teacher-preview-objective">
            {lesson.objective}
          </div>
        </div>

        {lesson.target_vocab?.length ? (
          <Section icon={<BookOpen size={14} />} label="Target words" testid="teacher-preview-vocab-section">
            <div className="flex flex-wrap gap-1.5">
              {lesson.target_vocab.map((w) => (
                <span
                  key={w}
                  className="rounded-full border border-white/20 bg-white/5 px-2.5 py-1 text-xs text-white/90"
                  data-testid="teacher-preview-vocab-chip"
                >
                  {w}
                </span>
              ))}
            </div>
          </Section>
        ) : (
          <div className="mt-4 text-xs text-white/50">
            <SpinnerGap size={12} className="inline animate-spin mr-1" />
            We'll prepare your vocabulary when you start.
          </div>
        )}

        {lesson.target_patterns?.length ? (
          <Section icon={<Target size={14} />} label="Sentence pattern" testid="teacher-preview-pattern-section">
            <div className="space-y-1.5">
              {lesson.target_patterns.map((p, i) => (
                <div key={i} className="font-mono text-sm text-white/95" data-testid="teacher-preview-pattern">
                  {p}
                </div>
              ))}
            </div>
          </Section>
        ) : null}

        {lesson.exit_ticket ? (
          <Section icon={<Lightning size={14} />} label="Challenge" testid="teacher-preview-challenge-section">
            <div className="text-sm text-white/90" data-testid="teacher-preview-challenge">
              {lesson.exit_ticket}
            </div>
          </Section>
        ) : null}
      </motion.div>

      {error ? (
        <div className="mt-4 text-sm text-[var(--lf-destructive)]" data-testid="teacher-preview-error">
          {error}
        </div>
      ) : null}

      <div className="mt-6 space-y-3">
        {/* Free-tier quota indicator. Shown only when the user is on
            the free plan (backend sends ``free_quota`` as null for Pro).
            Colour shifts to amber when the user has 1 lesson left to
            gently nudge conversion, and the CTA swaps to "Upgrade" when
            they're completely out. Keeps the daily-cap feeling like a
            taste of the product, not a wall. */}
        {freeQuota ? (
          freeQuota.remaining > 0 ? (
            <div
              className={`rounded-2xl border p-3 text-xs ${
                freeQuota.remaining === 1
                  ? 'border-amber-300 bg-amber-50 text-amber-900'
                  : 'border-[var(--lf-border)] bg-[var(--lf-surface-muted)] text-[var(--lf-text-muted)]'
              }`}
              data-testid="teacher-free-quota"
            >
              <strong className="font-semibold">
                {freeQuota.remaining} of {freeQuota.limit} free Teacher lessons
              </strong>{' '}
              remaining today.{' '}
              <Link to="/pricing" className="underline font-semibold text-[var(--lf-primary)]">
                Go unlimited →
              </Link>
            </div>
          ) : (
            <div
              className="rounded-2xl border border-[var(--lf-primary)] bg-[#FFF5EE] p-3 text-sm"
              data-testid="teacher-quota-exhausted"
            >
              <p className="font-semibold text-[var(--lf-text)]">
                You've used today's {freeQuota.limit} free lessons.
              </p>
              <p className="mt-1 text-xs text-[var(--lf-text-muted)]">
                Upgrade to Pro for unlimited Teacher-Mode lessons, or come
                back tomorrow.
              </p>
              <Link
                to="/pricing?reason=teacher_daily_cap"
                className="lf-btn lf-btn-primary w-full mt-3"
                data-testid="teacher-quota-upgrade-btn"
              >
                See plans →
              </Link>
            </div>
          )
        ) : null}

        <button
          onClick={startLesson}
          disabled={starting || (freeQuota && freeQuota.remaining === 0)}
          className="lf-btn lf-btn-primary w-full"
          data-testid="teacher-preview-start-btn"
        >
          {starting ? <SpinnerGap size={16} className="animate-spin" /> : (
            <>
              <ChalkboardTeacher size={16} weight="fill" /> Start lesson
              <ArrowRight size={14} weight="bold" />
            </>
          )}
        </button>
        <Link
          to="/teacher/notebook"
          className="lf-btn lf-btn-ghost w-full"
          data-testid="teacher-preview-notebook-link"
        >
          <Notebook size={14} weight="duotone" /> Lesson notebook
        </Link>
      </div>
    </div>
  );
}

function BackBar() {
  return (
    <div className="flex items-center gap-2">
      <Link
        to="/home"
        className="grid h-10 w-10 place-items-center rounded-full border border-[var(--lf-border)] bg-[var(--lf-surface)]"
        data-testid="teacher-preview-back"
      >
        <CaretLeft size={18} />
      </Link>
      <div>
        <div className="text-xs uppercase tracking-widest text-[var(--lf-text-muted)]">
          Teacher mode
        </div>
        <h2 className="text-lg font-bold">Lesson preview</h2>
      </div>
    </div>
  );
}

function Section({ icon, label, testid, children }) {
  return (
    <div className="mt-4" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#86d6b4]">
        {icon} {label}
      </div>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function ReasonBadge({ reason }) {
  const map = {
    holiday: { label: 'Seasonal lesson', color: 'bg-amber-100 text-amber-900 border-amber-200' },
    cefr: { label: `Recommended for your level`, color: 'bg-emerald-100 text-emerald-900 border-emerald-200' },
    any_fresh: { label: 'New lesson', color: 'bg-sky-100 text-sky-900 border-sky-200' },
    fallback: { label: 'Quick refresher', color: 'bg-slate-100 text-slate-800 border-slate-200' },
  };
  const key = reason.split(':')[0];
  const cfg = map[key] || map.any_fresh;
  return (
    <div
      className={`mt-3 inline-flex items-center gap-1.5 self-start rounded-full border px-2.5 py-1 text-xs font-medium ${cfg.color}`}
      data-testid="teacher-preview-reason"
    >
      {cfg.label}
    </div>
  );
}
